// The colours a player can pick as their own - twenty (twelve until online
// games went to forty seats, 2026-10-30; past twenty, repeats get a mark -
// see render/draw.js),
// or any hex code ('#3fa9f5') as a custom colour. A seat's colour is its
// player's favourite, unless someone seated earlier already has it - then
// the next free colour after it in this list, so a clash lands on a
// neighbour rather than somewhere random.

const rgb = (hex) => [16, 8, 0].map((s) => (parseInt(hex.slice(1), 16) >> s) & 255);
const toHex = (chans) => `#${chans.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;

export const COLOURS = [
  { key: 'coral', name: 'Coral', fill: '#ff6b5b' },
  { key: 'orange', name: 'Orange', fill: '#ff9f43' },
  { key: 'gold', name: 'Gold', fill: '#ffcc3d' },
  { key: 'lime', name: 'Lime', fill: '#a3e635' },
  { key: 'teal', name: 'Teal', fill: '#2ec4b6' },
  { key: 'sky', name: 'Sky', fill: '#4cc9f0' },
  { key: 'blue', name: 'Blue', fill: '#5b7cff' },
  { key: 'violet', name: 'Violet', fill: '#a58bff' },
  { key: 'magenta', name: 'Magenta', fill: '#d946ef' },
  { key: 'pink', name: 'Pink', fill: '#ff7eb6' },
  { key: 'tan', name: 'Tan', fill: '#c9a27e' },
  { key: 'silver', name: 'Silver', fill: '#cfd4e2' },
  // The second twelve... eight: spread round the wheel and in lightness from the first.
  { key: 'crimson', name: 'Crimson', fill: '#e63946' },
  { key: 'peach', name: 'Peach', fill: '#ffc4a8' },
  { key: 'butter', name: 'Butter', fill: '#fff09a' },
  { key: 'mint', name: 'Mint', fill: '#8ef0c0' },
  { key: 'emerald', name: 'Emerald', fill: '#1fae5b' },
  { key: 'steel', name: 'Steel', fill: '#7c93bd' },
  { key: 'plum', name: 'Plum', fill: '#8e44c9' },
  { key: 'white', name: 'White', fill: '#f7f7f2' },
].map((c) => ({ ...c, dim: darken(c.fill, 0.36) }));

const BY_KEY = Object.fromEntries(COLOURS.map((c) => [c.key, c]));

// Guests (and anyone without a favourite) are handed these in order.
export const GUEST_ORDER = ['coral', 'teal', 'gold', 'violet', 'sky', 'pink', 'lime', 'orange', 'blue', 'magenta', 'silver', 'tan', 'crimson', 'mint', 'plum', 'butter', 'emerald', 'peach', 'steel', 'white'];

// A custom colour is its own key: '#rrggbb', lower case. Accepts '3FA9F5',
// '#3af' and the like; anything else is null.
export function normaliseHex(text) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(text || '').trim());
  if (!m) return null;
  const h = m[1].length === 3 ? [...m[1]].map((d) => d + d).join('') : m[1];
  return `#${h.toLowerCase()}`;
}

export function isCustom(key) {
  return !BY_KEY[key] && !!normaliseHex(key);
}

const CUSTOM = {};

export function colour(key) {
  if (BY_KEY[key]) return BY_KEY[key];
  const hex = normaliseHex(key);
  if (!hex) return BY_KEY.coral;
  if (!CUSTOM[hex]) {
    const fill = brightEnough(hex);
    CUSTOM[hex] = { key: hex, name: 'Custom', fill, dim: darken(fill, 0.36) };
  }
  return CUSTOM[hex];
}

// How bright a colour looks (WCAG relative luminance, 0 black to 1 white).
export function luminance(hex) {
  const [r, g, b] = rgb(hex).map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// The well is near-black, so a very dark pick is mixed toward white until
// it shows up about as well as the darkest preset does.
const MIN_LUMINANCE = 0.15;
function brightEnough(hex) {
  const chans = rgb(hex);
  for (let t = 0; t <= 1; t += 0.02) {
    const out = toHex(chans.map((c) => c + (255 - c) * t));
    if (luminance(out) >= MIN_LUMINANCE) return out;
  }
  return '#ffffff';
}

// Locked blocks are drawn darker than falling ones.
function darken(hex, amount) {
  return toHex(rgb(hex).map((c) => c * (1 - amount)));
}

/**
 * wants: each seat's favourite colour key (a preset or a hex code), or null
 * for "no preference". A custom colour someone seated earlier already has
 * moves the later one to the next free preset.
 * Returns a colour key per seat, all different.
 */
export function assignColours(wants) {
  const taken = new Set();
  return wants.map((want) => {
    let pick = null;
    const hex = isCustom(want) ? normaliseHex(want) : null;
    if (hex) {
      pick = taken.has(hex) ? GUEST_ORDER.find((key) => !taken.has(key)) : hex;
    } else if (want && BY_KEY[want]) {
      const start = COLOURS.findIndex((c) => c.key === want);
      for (let i = 0; i < COLOURS.length && !pick; i += 1) {
        const key = COLOURS[(start + i) % COLOURS.length].key;
        if (!taken.has(key)) pick = key;
      }
    } else {
      pick = GUEST_ORDER.find((key) => !taken.has(key));
    }
    // Forty seats, twenty colours: past twenty, colours repeat, and the
    // renderer marks each repeat (a dot, a ring, a slash) so it still reads.
    if (!pick) pick = hex || (want && BY_KEY[want] ? want : GUEST_ORDER[taken.size % GUEST_ORDER.length]);
    taken.add(pick);
    return pick;
  });
}

// A colour for a newly added player that nobody else on the couch has yet.
export function freshColour(usedKeys) {
  const used = new Set(usedKeys);
  return GUEST_ORDER.find((k) => !used.has(k)) || 'coral';
}
